/**
 * ConciergeHandoffCard — the celebratory "found your match" card the
 * Concierge drops into the chat stream when it recommends an agent.
 *
 * Replaces the plain shared AgentCard inside the Concierge timeline with a
 * richer, tactile treatment:
 *   - an eyebrow line ("Found your match!") above the card;
 *   - the agent's textured brand identity staged prominently — the BotAvatar
 *     coin sits on a circular halo (a brand's avatar templates — the M²
 *     textures — are colored strokes on TRANSPARENT gaps, so the halo paints a
 *     neutral elevated disc UNDER the image or it would vanish), ringed by
 *     a thin brand-gradient rim;
 *   - name + reason/description, and a gradient CTA pill
 *     ("Chat with {name}") with an arrow that nudges forward on hover
 *     (and flips under RTL).
 *
 * The whole card is ONE <button>. Clicking it does not navigate instantly:
 * a ~320ms "handoff beat" plays first (card pulse + rim flash via the
 * `--departing` class; the host panel fades the rest of its content via
 * `onDepartStart`), then `onChoose(chatbotId)` fires the real routing.
 * Double-clicks are guarded; under prefers-reduced-motion we route
 * immediately with no beat.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { BotAvatar } from '@/components/common/BotAvatar';
import { prefersReducedMotion } from './motion';
import './ConciergeHandoffCard.css';

export interface ConciergeHandoffCardProps {
  chatbotId: string;
  name: string;
  description?: string | null;
  avatarColor?: string | null;
  avatarVariant?: number | null;
  /** One-line "why this agent" note from the Concierge (wins over description). */
  reason?: string | null;
  /** Fired the instant the handoff beat starts, so the host can fade the panel. */
  onDepartStart?: () => void;
  /** Fired when it's time to actually route (after the beat, or immediately
   *  under prefers-reduced-motion). */
  onChoose: (chatbotId: string) => void;
}

/** Duration of the pre-navigation handoff beat (must match the CSS). */
const HANDOFF_BEAT_MS = 320;

export function ConciergeHandoffCard({
  chatbotId,
  name,
  description,
  avatarColor,
  avatarVariant,
  reason,
  onDepartStart,
  onChoose,
}: ConciergeHandoffCardProps) {
  const { t } = useLanguage();
  const [departing, setDeparting] = useState(false);
  const chosenRef = useRef(false);
  // The handoff beat is cancelled if the host surface unmounts mid-beat
  // (e.g. Esc/backdrop close within the 320ms window) — otherwise the
  // pending timeout would navigate the user away AFTER they dismissed.
  const departTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (departTimerRef.current !== null) {
        window.clearTimeout(departTimerRef.current);
      }
    },
    [],
  );

  const handleClick = useCallback(() => {
    if (chosenRef.current) return; // double-click guard
    chosenRef.current = true;
    if (prefersReducedMotion()) {
      onChoose(chatbotId);
      return;
    }
    setDeparting(true);
    onDepartStart?.();
    departTimerRef.current = window.setTimeout(
      () => onChoose(chatbotId),
      HANDOFF_BEAT_MS,
    );
  }, [chatbotId, onChoose, onDepartStart]);

  return (
    <div
      className={`concierge-handoff${departing ? ' concierge-handoff--departing' : ''}`}
    >
      <div className="concierge-handoff-eyebrow">
        <svg
          className="concierge-handoff-spark"
          aria-hidden="true"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2c.6 4.8 2.9 7.6 8 8-5.1.4-7.4 3.2-8 8-.6-4.8-2.9-7.6-8-8 5.1-.4 7.4-3.2 8-8z" />
          <path d="M19.5 14.5c.25 2 1.2 3.2 3.3 3.3-2.1.2-3.05 1.4-3.3 3.4-.25-2-1.2-3.2-3.3-3.4 2.1-.1 3.05-1.3 3.3-3.3z" opacity="0.75" />
        </svg>
        <span>{t('concierge.handoff.eyebrow')}</span>
      </div>

      <button
        type="button"
        className="concierge-handoff-card"
        onClick={handleClick}
        data-testid={`concierge-handoff-${chatbotId}`}
        title={name}
      >
        {/* Avatar tile — mirrors the home carousel / AgentCard visual exactly
            (white rounded tile + the agent's textured coin, nothing layered
            on top), so the agent looks identical wherever it's offered. */}
        <span className="concierge-handoff-tile" aria-hidden="true">
          <BotAvatar
            name={name || 'AI'}
            size={48}
            colorOverride={avatarColor || undefined}
            variantOverride={avatarVariant ?? undefined}
          />
        </span>

        <span className="concierge-handoff-body">
          <span className="concierge-handoff-name">{name}</span>
          {(reason || description) && (
            <span dir="auto" className="concierge-handoff-desc bidi-clamp">{reason || description}</span>
          )}
          <span className="concierge-handoff-cta">
            <span dir="auto" className="concierge-handoff-cta-label">
              {t('concierge.handoff.cta', { name })}
            </span>
            <svg
              className="concierge-handoff-arrow"
              aria-hidden="true"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14" />
              <path d="m13 6 6 6-6 6" />
            </svg>
          </span>
        </span>
      </button>
    </div>
  );
}
